import type { Feature } from 'ol';
import { getWorldsAway } from 'ol/coordinate';
import { getCenter, getWidth } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import type { Geometry } from 'ol/geom';
import SimpleGeometry from 'ol/geom/SimpleGeometry';
import type VectorLayer from 'ol/layer/Vector';
import type VectorSource from 'ol/source/Vector';
import { v4 as uuid } from 'uuid';
import { computed } from 'vue';

import type {
  DrawLayerGeometryPatchDTO,
  GeoJsonFeatureDTO,
} from '@nora-monorepo/nora-api-client';
import {
  DrawLayerTypeEnum,
  DrawToolType,
} from '@nora-monorepo/nora-api-client';
import {
  isNonEmptyString,
  NoraSupportedCoordinateFormats,
} from '@nora-monorepo/shared';

import type { BaseDrawTool } from '#nora-ui/map/drawtools/base-draw-tool';
import type { DrawToolOptions, UndoRedoFeatureAction } from '#nora-ui/map/drawtools/types';
import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';

export class DrawToolManager {
  vectorSource: VectorSource;
  vectorLayer: VectorLayer<VectorSource>;

  /**
   * The draw tool the user has selected
   */
  currentTool: BaseDrawTool | null = null;

  drawStyleOptions: DrawToolOptions = {
    drawToolType: DrawToolType.unstyled,
  };

  undoStack: UndoRedoFeatureAction[] = [];
  redoStack: UndoRedoFeatureAction[] = [];

  readonly isActiveDrawLayerLocked = computed(() => {
    const drawLayer = this.map.state.draw.activeDrawLayer.value;

    return drawLayer !== undefined
      && drawLayer.operationId === this.map.ref.activeOperationId
      && drawLayer.locked;
  });

  private canSaveFeatureToDrawLayer(drawLayerId: string): boolean {
    const targetDrawLayer = this.map.state.draw.getDrawLayerById(drawLayerId);

    if (!targetDrawLayer) {
      return false;
    }

    if (targetDrawLayer.locked) {
      return false;
    }

    if (targetDrawLayer.type === DrawLayerTypeEnum.GEOFENCE) {
      return false;
    }

    return this.map.ref.isOperationVisible(targetDrawLayer.operationId);
  }

  /** New drawings use the active layer; edits may save to a feature's source layer instead. */
  readonly canDraw = computed(() => {
    const drawLayerId = this.map.state.draw.activeDrawLayerId.value;

    return isNonEmptyString(drawLayerId)
      && this.canSaveFeatureToDrawLayer(drawLayerId)
      && this.map.state.draw.activeDrawLayer.value?.operationId
      === this.map.ref.activeOperationId;
  });

  constructor(
    readonly map: NoraMap,
    vectorSource: VectorSource,
    vectorLayer: VectorLayer<VectorSource>,
  ) {
    this.vectorSource = vectorSource;
    this.vectorLayer = vectorLayer;
  }

  deactivate(): void {
    this.currentTool?.deactivate();
  }

  setDrawTool(newTool: BaseDrawTool | null | undefined): void {
    if (this.currentTool) {
      this.currentTool.deactivate();
    }

    if (newTool) {
      this.currentTool = newTool;
      this.currentTool.activate();
    }
    else {
      this.currentTool = null;
    }
  }

  updateDrawStyleOptions(options: DrawToolOptions): void {
    this.setDrawStyleOptions({
      ...this.drawStyleOptions,
      ...options,
    });
    this.currentTool?.onStylingUpdated();
  }

  /**
   * Updates the active Draw Layer, or the provided draw layer id, with the new Feature,
   * and also sends update to backend.
   */
  async saveFeature(
    feature: Feature<Geometry>,
    moveToDrawLayerId?: string,
  ): Promise<Feature<Geometry>> {
    const targetDrawLayerId = this.resolveTargetDrawLayerId(
      feature,
      moveToDrawLayerId,
    );

    if (!isNonEmptyString(targetDrawLayerId)) {
      throw new Error(`No active draw layer, can't save feature`);
    }

    if (!this.canSaveFeatureToDrawLayer(targetDrawLayerId)) {
      throw new Error(`Cannot save feature to draw layer ${targetDrawLayerId}`);
    }

    // A derived default target never went through setActiveDrawLayer's visibility side
    // effect; a feature saved to a hidden layer would silently not render.
    if (!this.map.state.draw.isDrawLayerVisible(targetDrawLayerId)) {
      this.map.state.draw.showDrawLayer(targetDrawLayerId);
    }

    // Get the current feature ID or generate a new one
    const existingFeatureId = feature.get('id') as string | undefined;
    const featureId: string = existingFeatureId ?? uuid();
    feature.setProperties({
      ...feature.getProperties(),
      drawLayerId: targetDrawLayerId,
      id: featureId,
    });

    // Cloning the feature to avoid the feature visibly shifting before save finishes
    const featureToPersist = feature.clone();
    this.normalizeFeatureGeometryToValidRange(featureToPersist);

    const geojsonFormat = new GeoJSON();
    const geoJsonFeature = geojsonFormat.writeFeatureObject(featureToPersist, {
      dataProjection: NoraSupportedCoordinateFormats.WGS84,
      featureProjection: NoraSupportedCoordinateFormats.WEB_MERCATOR,
    });

    const patch: DrawLayerGeometryPatchDTO = {};

    if (!('coordinates' in geoJsonFeature.geometry)) {
      throw new Error('This function does not support geometryCollections');
    }

    const featureToSave: GeoJsonFeatureDTO & { id: string } = {
      ...geoJsonFeature,
      id: featureId,
      properties: {
        id: featureId,
        ...geoJsonFeature.properties,
      },
      geometry: {
        ...geoJsonFeature.geometry,
      },
    };

    if (isNonEmptyString(existingFeatureId) && !isNonEmptyString(moveToDrawLayerId)) {
      patch.update = [featureToSave];
    }
    else {
      patch.add = [featureToSave];
    }

    await this.map.state.draw.updateDrawLayerGeometry(
      patch,
      targetDrawLayerId,
    );

    const geoJsonFeatureFormatted = geojsonFormat.readFeature(featureToSave, {
      dataProjection: NoraSupportedCoordinateFormats.WGS84,
      featureProjection: NoraSupportedCoordinateFormats.WEB_MERCATOR,
    });

    if (Array.isArray(geoJsonFeatureFormatted)) {
      throw new TypeError('This function does not support geometryCollections');
    }

    geoJsonFeatureFormatted.setId(featureToSave.id);

    if (isNonEmptyString(existingFeatureId)) {
      this.map.selectDrawFeature(geoJsonFeatureFormatted);
    }

    return geoJsonFeatureFormatted;
  }

  /**
   * Normalizes a drawn feature geometry location into a valid range [-180, 180] before saving.
   *
   * The map can repeat horizontally, so a feature may be drawn in a wrapped world.
   * This shifts it into a valid range regardless of how many times you panned the globe.
   */
  private normalizeFeatureGeometryToValidRange(feature: Feature<Geometry>): void {
    const geometry = feature.getGeometry();

    if (!geometry || !(geometry instanceof SimpleGeometry)) {
      return;
    }

    const projection = this.map.getOlMapOrThrow().getView().getProjection();

    if (!projection.canWrapX()) {
      return;
    }

    const projectionExtent = projection.getExtent();
    const worldWidth = getWidth(projectionExtent);
    const geometryExtent = geometry.getExtent();
    const [geometryCenterX] = getCenter(geometryExtent);

    const worldsAway = getWorldsAway([geometryCenterX, 0], projection, worldWidth);

    if (worldsAway === 0) {
      return;
    }

    geometry.translate(-worldsAway * worldWidth, 0);
  }

  async removeFeature(
    featureOrFeatureId: Feature<Geometry> | string,
    drawLayerId?: string,
  ): Promise<void> {
    const targetDrawLayerId = this.resolveTargetDrawLayerId(
      featureOrFeatureId,
      drawLayerId,
    );

    if (!isNonEmptyString(targetDrawLayerId)) {
      throw new Error('No active draw layer, can\'t remove feature');
    }

    let featureId: string | undefined;
    if (typeof featureOrFeatureId === 'string') {
      featureId = featureOrFeatureId;
    }
    else {
      featureId = featureOrFeatureId?.get('id') as string | undefined;
    }

    if (!isNonEmptyString(featureId)) {
      throw new Error('Feature does not have an id');
    }

    await this.map.state.draw.updateDrawLayerGeometry(
      {
        remove: [featureId],
      },
      targetDrawLayerId,
    );

    this.map.layers.removeFeatureFromMap(featureOrFeatureId);
  }

  setDrawStyleOptions(options: DrawToolOptions): void {
    this.drawStyleOptions = options;
  }

  addToUndoStack(undoAction: UndoRedoFeatureAction): void {
    this.undoStack.push(undoAction);
    this.clearRedoStack();
  }

  undo(): void {
    const undoAction = this.undoStack.pop();
    if (!undoAction) {
      return;
    }

    if (undoAction.type === 'add') {
      for (const feature of undoAction.features) {
        this.vectorSource.removeFeature(feature);
      }
      this.redoStack.push(undoAction);
    }
    else if (undoAction.type === 'remove') {
      for (const feature of undoAction.features) {
        this.vectorSource.addFeature(feature);
      }
      this.redoStack.push(undoAction);
    }
  }

  redo(): void {
    const redoAction = this.redoStack.pop();
    if (!redoAction) {
      return;
    }

    if (redoAction.type === 'add') {
      for (const feature of redoAction.features) {
        this.vectorSource.addFeature(feature);
      }
      this.undoStack.push(redoAction);
    }
    else if (redoAction.type === 'remove') {
      for (const feature of redoAction.features) {
        this.vectorSource.removeFeature(feature);
      }
      this.undoStack.push(redoAction);
    }
  }

  clearRedoStack(): void {
    this.redoStack.length = 0;
  }

  private getFeatureDrawLayerId(feature: Feature<Geometry>): string | undefined {
    const { drawLayerId } = getFeatureProperties<{ drawLayerId?: string }>(feature);

    return isNonEmptyString(drawLayerId) ? drawLayerId : undefined;
  }

  /**
   * Resolves the draw layer to use for a feature or feature ID.
   *
   * Resolution order:
   * 1. Use `explicitDrawLayerId` when provided.
   * 2. For an existing feature, use the feature's source draw layer when available.
   * 3. Otherwise, use the currently active draw layer.
   *
   * @returns The resolved draw layer ID, or `undefined` if no draw layer can be resolved.
   */
  private resolveTargetDrawLayerId(
    featureOrFeatureId: Feature<Geometry> | string,
    explicitDrawLayerId?: string,
  ): string | undefined {
    if (explicitDrawLayerId !== undefined) {
      return explicitDrawLayerId;
    }

    if (typeof featureOrFeatureId !== 'string') {
      return this.getFeatureDrawLayerId(featureOrFeatureId)
        ?? this.map.state.draw.activeDrawLayerId.value;
    }

    return this.map.state.draw.activeDrawLayerId.value;
  }
}
